import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const MaskedWord = ({ word, i }) => (
  <span className="mr-[0.28em] inline-block overflow-hidden align-bottom">
    <motion.span
      className="inline-block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay: 0.35 + i * 0.06 }}
    >
      {word}
    </motion.span>
  </span>
);

export const KineticHero = ({ data, ui, image }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  const words = data.title.split(" ");

  return (
    <header ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden px-5 pb-16 pt-28 md:px-10 md:pt-32">
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 items-end gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        {/* Title block */}
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.35em]"
            style={{ color: "var(--accent)" }}
          >
            <span className="inline-block h-px w-10" style={{ background: "var(--accent)" }} />
            {data.eyebrow}
          </motion.p>

          <h1 className="font-display font-black leading-[0.92] tracking-tight text-[13vw] sm:text-[11vw] lg:text-[6.6vw]">
            {words.map((w, i) => (
              <MaskedWord key={`${w}-${i}`} word={w} i={i} />
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 max-w-xl"
          >
            <p className="text-base leading-relaxed md:text-lg" style={{ color: "var(--muted)" }}>
              {data.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
              <span>{data.byline}</span>
              <span style={{ color: "var(--accent)" }}>·</span>
              <span>{data.location}</span>
              <span style={{ color: "var(--accent)" }}>·</span>
              <span>{data.readingTime}</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Arched parallax photo */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
          className="relative h-[52vh] w-full overflow-hidden rounded-t-[999px] lg:h-[76vh]"
          style={{ background: "var(--paper)" }}
        >
          <motion.img
            src={image}
            alt=""
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 h-full w-full object-cover will-change-transform"
          />
          <div className="pointer-events-none absolute inset-0 rounded-t-[999px] ring-1 ring-inset" style={{ borderColor: "var(--line)" }} />
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="mx-auto mt-12 flex max-w-[1500px] items-center gap-3 text-xs uppercase tracking-[0.3em]"
        style={{ color: "var(--muted)" }}
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
        {ui.scroll}
      </motion.div>
    </header>
  );
};
